/**
 * CatalogBrowse — GVW-120 STAC catalog browse
 * 
 * Per brief.md: WorkspaceShell with center = catalog grid; left rail = facet filter;
 * right panel = selected item summary card. Follows commercial SaaS marketplace pattern.
 */

import React, { useState, useEffect } from 'react';
import { catalogClient, type Layer, type CatalogResult } from '../../catalog/catalogClient';

interface CatalogBrowseProps {
  className?: string;
}

interface CatalogBrowseState {
  layers: Layer[];
  loading: boolean;
  error: string | null;
  selectedLayer: Layer | null;
  searchQuery: string;
}

export function CatalogBrowse({ className }: CatalogBrowseProps): JSX.Element {
  const [state, setState] = useState<CatalogBrowseState>({
    layers: [],
    loading: true,
    error: null,
    selectedLayer: null,
    searchQuery: '',
  });

  const loadLayers = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    const result: CatalogResult<Layer[]> = await catalogClient.listLayers();
    
    if (result.ok) {
      setState(prev => ({
        ...prev,
        layers: result.data,
        loading: false,
      }));
    } else {
      let errorMessage = 'Failed to load catalog';
      if (result.reason === 'backend-not-deployed') {
        errorMessage = 'Backend not deployed. The catalog service is not available.';
      } else if (result.reason === 'network-error') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (result.reason === 'unauthorized') {
        errorMessage = 'You do not have permission to access the catalog.';
      }
      
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  useEffect(() => {
    loadLayers();
  }, []);

  const handleLayerSelect = (layer: Layer) => {
    setState(prev => ({ ...prev, selectedLayer: layer }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, searchQuery: event.target.value }));
  };

  const filteredLayers = state.layers.filter(layer =>
    layer.name.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
    (layer.description?.toLowerCase().includes(state.searchQuery.toLowerCase()) ?? false)
  );

  return (
    <div className={`catalog-browse ${className || ''}`} data-testid="catalog-browse">
      {/* Header with search */}
      <div className="catalog-browse__header">
        <h1>
          <span className="material-symbols-outlined" aria-hidden="true">dataset</span>
          Catalog
        </h1>
        
        <div className="catalog-browse__search">
          <span className="material-symbols-outlined" aria-hidden="true">search</span>
          <input
            type="text"
            placeholder="Search datasets..."
            value={state.searchQuery}
            onChange={handleSearchChange}
            aria-label="Search datasets"
            aria-keyshortcuts="/"
          />
        </div>
      </div>

      <div className="catalog-browse__content">
        {/* Left rail - facet filters */}
        <div className="catalog-browse__facets" role="complementary" aria-label="Filters">
          <h2>
            <span className="material-symbols-outlined" aria-hidden="true">filter_list</span>
            Filters
          </h2>
          
          <div className="catalog-browse__facet-group">
            <h3>Type</h3>
            <label>
              <input type="checkbox" /> Raster
            </label>
            <label>
              <input type="checkbox" /> Vector
            </label>
            <label>
              <input type="checkbox" /> Point Cloud
            </label>
            <label>
              <input type="checkbox" /> 3D Tiles
            </label>
          </div>
          
          <div className="catalog-browse__facet-group">
            <h3>Time Range</h3>
            <input type="date" aria-label="Start date" />
            <input type="date" aria-label="End date" />
          </div>
        </div>

        {/* Center - catalog grid */}
        <main className="catalog-browse__main">
          {state.loading && (
            <div className="catalog-browse__loading" aria-busy="true">
              <div className="catalog-browse__skeleton">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="catalog-browse__skeleton-card" />
                ))}
              </div>
            </div>
          )}

          {state.error && (
            <div className="catalog-browse__error" role="alert">
              <span className="material-symbols-outlined" aria-hidden="true">error</span>
              <h2>We could not load the catalog</h2>
              <p>{state.error}</p>
              <button onClick={loadLayers} type="button">
                Try again
              </button>
            </div>
          )}

          {!state.loading && !state.error && filteredLayers.length === 0 && (
            <div className="catalog-browse__empty">
              <span className="material-symbols-outlined" aria-hidden="true">dataset</span>
              <h2>No items match your filters</h2>
              <button 
                onClick={() => setState(prev => ({ ...prev, searchQuery: '' }))}
                type="button"
              >
                Reset filters
              </button>
            </div>
          )}

          {!state.loading && !state.error && filteredLayers.length > 0 && (
            <div className="catalog-browse__grid" role="list">
              {filteredLayers.map((layer) => (
                <div
                  key={layer.id}
                  className={`catalog-browse__card ${
                    state.selectedLayer?.id === layer.id ? 'catalog-browse__card--selected' : ''
                  }`}
                  role="listitem"
                  tabIndex={0}
                  onClick={() => handleLayerSelect(layer)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLayerSelect(layer);
                    }
                  }}
                  aria-label={`Dataset: ${layer.name}`}
                >
                  <div className="catalog-browse__card-header">
                    <h3>{layer.name}</h3>
                    <span className="catalog-browse__card-type">{layer.type}</span>
                  </div>
                  
                  {layer.description && (
                    <p className="catalog-browse__card-description">
                      {layer.description}
                    </p>
                  )}
                  
                  <div className="catalog-browse__card-actions">
                    <button
                      type="button"
                      className="catalog-browse__add-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: Implement add to current view
                        console.log('Add layer:', layer.id);
                      }}
                      aria-label={`Add ${layer.name} to current view`}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">add</span>
                      Add to current view
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* Right panel - selected item summary */}
        {state.selectedLayer && (
          <aside className="catalog-browse__summary" role="complementary" aria-labelledby="summary-heading">
            <h2 id="summary-heading">Dataset Details</h2>
            
            <div className="catalog-browse__summary-content">
              <h3>{state.selectedLayer.name}</h3>
              <p><strong>Type:</strong> {state.selectedLayer.type}</p>
              <p><strong>Format:</strong> {state.selectedLayer.format}</p>
              
              {state.selectedLayer.description && (
                <div>
                  <strong>Description:</strong>
                  <p>{state.selectedLayer.description}</p>
                </div>
              )}
              
              {state.selectedLayer.bbox && (
                <div>
                  <strong>Extent:</strong>
                  <p>
                    [{state.selectedLayer.bbox[0].toFixed(3)}, {state.selectedLayer.bbox[1].toFixed(3)}] to
                    [{state.selectedLayer.bbox[2].toFixed(3)}, {state.selectedLayer.bbox[3].toFixed(3)}]
                  </p>
                </div>
              )}
              
              <div className="catalog-browse__summary-actions">
                <button
                  type="button"
                  onClick={() => {
                    // TODO: Navigate to dataset detail page
                    console.log('View details:', state.selectedLayer?.id);
                  }}
                >
                  View Full Details
                </button>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}